import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { usePageMeta, EMAIL } from '../utils/seo';
import { errorStyle } from '../components/formStyles';

const label = { color: '#c89b3c', letterSpacing: '4px', fontSize: '12px', textTransform: 'uppercase', margin: '0 0 10px' };
const h1 = { fontFamily: 'var(--font-heading)', color: '#f0e6cc', fontSize: 'clamp(32px, 6vw, 48px)', letterSpacing: '3px', textTransform: 'uppercase', margin: '0 0 16px' };
const kr = (n) => `${Number(n).toLocaleString('en-GB').replace(/,/g, '.')} kr.`;
const dt = { color: '#c89b3c', fontFamily: 'var(--font-heading)', letterSpacing: '2px', textTransform: 'uppercase', fontSize: '13px', paddingTop: '3px' };

const Box = ({ children }) => (
    <div style={{ background: '#0a0a0a', padding: '60px 20px 120px' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto', border: '1px solid var(--color-gold)', padding: 'clamp(28px, 6vw, 48px)', background: 'rgba(20,20,20,0.8)' }}>{children}</div>
    </div>
);

/** /pay/:token — a payment link made in /admin/payments. Icelandic: the payer has a kennitala. */
const PayLink = () => {
    const { token } = useParams();
    const { search } = useLocation();
    const returned = /done=1/.test(search);
    const cancelled = /cancelled=1|failed=1/.test(search);
    usePageMeta({ title: 'Greiðsla', path: '/pay' });

    const [link, setLink] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const polls = useRef(0);

    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
        return () => meta.remove();
    }, []);

    useEffect(() => {
        let timer;
        let alive = true;
        const load = async () => {
            try {
                const r = await fetch(`/api/pay-link?token=${encodeURIComponent(token)}`);
                const j = await r.json();
                if (!r.ok) throw new Error(j.error || 'Hlekkurinn er ekki gildur.');
                if (!alive) return;
                setLink(j.link);
                // Back from Teya: keep asking for up to ~2 minutes until the payment is confirmed
                if (returned && j.link.status === 'open' && polls.current++ < 40) timer = setTimeout(load, 3000);
            } catch (e) { if (alive) setError(e.message); }
        };
        load();
        return () => { alive = false; clearTimeout(timer); };
    }, [token, returned]);

    const pay = async () => {
        setBusy(true); setError(null);
        try {
            const r = await fetch('/api/pay-link', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) });
            const j = await r.json();
            if (!r.ok) throw new Error(j.error || 'Ekki tókst að opna greiðslusíðuna.');
            if (j.alreadyPaid) { setLink(j.link); setBusy(false); return; }
            window.location.href = j.checkoutUrl;
        } catch (e) { setError(e.message); setBusy(false); }
    };

    if (error && !link) return <Box><h1 style={h1}>Hlekkur ekki gildur</h1><p style={{ color: '#ccc', lineHeight: 1.7 }}>{error} Sendu póst á <a href={`mailto:${EMAIL}`} className="text-gold">{EMAIL}</a> ef þig vantar aðstoð.</p></Box>;
    if (!link) return <Box><p className="text-gold" style={{ letterSpacing: '3px' }}>AUGNABLIK…</p></Box>;

    const details = (
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 20px', margin: '0 0 28px', color: '#e8dcc8', fontSize: '17px' }}>
            <dt style={dt}>Greiðandi</dt><dd style={{ margin: 0 }}>{link.payer_name}<br /><span style={{ color: '#888', fontSize: '14px' }}>kt. {link.kennitala}</span></dd>
            <dt style={dt}>Fyrir</dt><dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{link.reason}</dd>
            <dt style={dt}>Upphæð</dt><dd style={{ margin: 0, fontFamily: 'var(--font-heading)', fontSize: '26px', color: '#f0e6cc' }}>{kr(link.amount_isk)}</dd>
            <dt style={dt}>Tilvísun</dt><dd style={{ margin: 0, letterSpacing: '2px' }}>{link.ref}</dd>
        </dl>
    );

    if (link.status === 'paid') {
        return (
            <Box>
                <p style={label}>Greiðsla móttekin</p>
                <h1 style={h1}>Takk fyrir</h1>
                {details}
                <p style={{ color: '#ccc', lineHeight: 1.7, margin: 0 }}>Greiðslan hefur borist Dillon Whiskey Bar. Kvittun frá Teya berst í tölvupósti ef netfang var gefið upp.</p>
            </Box>
        );
    }

    if (link.status === 'cancelled') {
        return (
            <Box>
                <p style={label}>Hlekkur ógildur</p>
                <h1 style={h1}>Þessi greiðsla var felld niður</h1>
                <p style={{ color: '#ccc', lineHeight: 1.7 }}>Hafðu samband á <a href={`mailto:${EMAIL}`} className="text-gold">{EMAIL}</a> ef þú átt von á að greiða.</p>
            </Box>
        );
    }

    if (returned && polls.current < 40) {
        return (
            <Box>
                <p style={label}>Augnablik</p>
                <h1 style={h1}>Staðfesti greiðslu…</h1>
                <p style={{ color: '#ccc', lineHeight: 1.7 }}>Beðið er eftir staðfestingu frá kortafyrirtækinu. Það tekur yfirleitt örfáar sekúndur.</p>
            </Box>
        );
    }

    return (
        <Box>
            <p style={label}>{cancelled ? 'Greiðsla kláraðist ekki' : 'Dillon Whiskey Bar'}</p>
            <h1 style={h1}>Greiðsla</h1>
            {cancelled && <p style={{ color: '#e0a44b', lineHeight: 1.6, margin: '0 0 20px' }}>Greiðslan fór ekki í gegn. Þú getur reynt aftur.</p>}
            {details}
            {error && <p style={errorStyle}>{error}</p>}
            <button type="button" className="btn btn-primary" onClick={pay} disabled={busy} style={{ width: '100%' }}>{busy ? 'Opna greiðslusíðu…' : `Greiða ${kr(link.amount_isk)}`}</button>
            <p style={{ color: '#777', fontSize: '13px', margin: '16px 0 0', lineHeight: 1.6 }}>Greitt er með korti í öruggri greiðslusíðu Teya. Dillon Whiskey Bar, Laugavegur 30, 101 Reykjavík.</p>
        </Box>
    );
};

export default PayLink;
